import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Users, BookOpen, Handshake, FileText, GraduationCap, Lightbulb, HeartHandshake, Landmark } from "lucide-react";

const ABOUT_IMG = "https://images.pexels.com/photos/7108760/pexels-photo-7108760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MTL_IMG = "https://images.unsplash.com/photo-1728228340487-f7061d206650?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtb250cmVhbCUyMGFyY2hpdGVjdHVyZSUyMHN0cmVldHxlbnwwfHx8fDE3ODgzOTQ5NTl8MA&ixlib=rb-4.1.0&q=85";

const leviers = [
  { num: "01", icone: GraduationCap, titre: "Éduquer", texte: "Développer les connaissances et compétences juridiques, financières, fiscales, stratégiques et opérationnelles." },
  { num: "02", icone: Lightbulb, titre: "Donner accès à l'expertise", texte: "Mobiliser des expertises spécialisées et multidisciplinaires autrement difficiles d'accès." },
  { num: "03", icone: HeartHandshake, titre: "Soutenir", texte: "Offrir des bourses, ressources, mentorat et mesures d'accompagnement aux personnes et organisations admissibles." },
  { num: "04", icone: Landmark, titre: "Financer", texte: "Mobiliser des ressources financières permettant de soutenir des bénéficiaires, projets et organisations admissibles dans le respect des fins de la Fondation." },
];

const activites = [
  { icone: BookOpen, titre: "Formations et ateliers", texte: "Des formations gratuites et à tarif modique sur le droit des affaires, la fiscalité et la gestion d'entreprise, offertes en ligne et en présentiel partout à Montréal." },
  { icone: Users, titre: "Cliniques d'information gratuites", texte: "Des séances d'information juridique et fiscale animées par des professionnels bénévoles, pour répondre aux questions concrètes des entrepreneurs et des particuliers." },
  { icone: Handshake, titre: "Mentorat entrepreneurial", texte: "Un programme de jumelage entre entrepreneurs en démarrage et mentors d'expérience, pour transformer les idées en projets viables." },
  { icone: FileText, titre: "Vulgarisation et publications", texte: "Des articles, guides et gabarits en langage clair, publiés régulièrement pour rendre le savoir juridique et fiscal accessible au plus grand nombre." },
];

export default function About() {
  return (
    <>
      <section className="bg-espresso py-28 text-ivory md:py-36" data-testid="about-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">À propos de la Fondation</p>
              <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="about-title">
                L'expertise et les ressources au service de <span className="italic text-primary">l'autonomie</span>.
              </h1>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-ivory/70 md:text-lg">
                La Fondation HORA est un organisme à but non lucratif québécois qui œuvre à
                l'autonomie économique et à la résilience des personnes et des organisations,
                particulièrement celles en situation de vulnérabilité.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative hidden lg:col-span-5 lg:block">
            <div className="absolute -right-5 -top-5 h-full w-full border border-gold/40" aria-hidden="true" />
            <img
              src={ABOUT_IMG}
              alt="Professionnelles et professionnels collaborant en atelier"
              className="relative aspect-[4/5] w-full object-cover"
              fetchPriority="high"
              decoding="async"
              data-testid="about-hero-image"
            />
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="bg-background py-28" data-testid="about-mission">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Notre mission</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight">
              Démocratiser l'accès aux moyens de réussir
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <div className="space-y-6 text-base leading-relaxed text-foreground/70 md:text-lg">
              <p>
                La Fondation HORA a pour mission de favoriser l'autonomie économique et la
                résilience des personnes et des organisations en démocratisant l'accès à
                l'éducation, à l'expertise spécialisée, aux ressources et au soutien financier
                nécessaires pour développer leurs capacités, prendre des décisions éclairées et
                bâtir des activités durables et porteuses d'impact.
              </p>
              <p>
                Nous croyons qu'aucun projet ne devrait échouer faute d'accès aux connaissances ou
                aux ressources. Le droit, la fiscalité et la stratégie sont des outils de protection
                et d'émancipation — encore faut-il pouvoir s'en saisir.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-card py-28" data-testid="about-leviers">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Comment? Par quatre leviers</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Notre architecture d'action
            </h2>
          </Reveal>
          <div className="mt-16 space-y-0">
            {leviers.map((l, i) => (
              <Reveal key={l.num} delay={i * 0.08}>
                <div className="grid items-start gap-6 border-t border-border py-12 md:grid-cols-12" data-testid={`levier-${l.num}`}>
                  <span className="font-serif text-6xl font-medium text-gold md:col-span-2 md:text-7xl">{l.num}</span>
                  <div className="md:col-span-4">
                    <l.icone size={28} strokeWidth={1.5} className="text-primary" />
                    <h3 className="mt-4 font-serif text-2xl font-semibold uppercase tracking-wide md:text-3xl">{l.titre}</h3>
                  </div>
                  <p className="text-base leading-relaxed text-foreground/70 md:col-span-5 md:col-start-8 md:text-lg">{l.texte}</p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-border" />
          </div>
        </div>
      </section>

      <section className="bg-espresso py-28 text-ivory" data-testid="about-vision">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Notre vision</p>
            <blockquote className="mt-8 max-w-4xl font-serif text-3xl font-medium leading-[1.3] tracking-tight md:text-5xl" data-testid="about-vision-text">
              Bâtir une société où l'accès à l'expertise, aux connaissances et aux ressources
              nécessaires à la réussite économique ne dépend plus des moyens dont on dispose, mais
              du <span className="italic text-primary">potentiel</span> que l'on peut développer et
              de <span className="italic text-gold">l'impact</span> que l'on peut créer.
            </blockquote>
            <p className="mt-10 max-w-3xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Faire en sorte que l'accès aux moyens nécessaires pour bâtir et pérenniser une
              activité économique ou une organisation ne soit plus réservé à ceux qui ont déjà les
              ressources et les réseaux nécessaires pour y accéder.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-28" data-testid="about-gouvernance">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Gouvernance</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Conseil d'administration
            </h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-foreground/70" data-testid="gouvernance-intro">
              La Fondation HORA est une personne morale sans but lucratif constituée le 24 février
              2021 (NEQ 1176331792). Elle était anciennement connue sous le nom de Clinique
              juridique d'affaires (Business Legal Clinic). Elle est dirigée par un conseil
              d'administration composé de :
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" data-testid="conseil-administration">
            {[
              { nom: "Mireille Lavie Nana", role: "Fondatrice, présidente et directrice générale", profession: "Avocate en droit des affaires — Hora Avocats", depuis: "2021", photo: "https://customer-assets-rejwkqb3.emergentagent.net/job_hora-resilience/artifacts/xhi0c9kk_BE404132.webp" },
              { nom: "Hanitra Ravalimanantsoa", role: "Secrétaire", profession: "Avocate en droit des affaires — RDS Avocats", depuis: "2022", photo: "https://customer-assets-rejwkqb3.emergentagent.net/job_hora-resilience/artifacts/hl2sadip_BE404100.webp" },
              { nom: "Anne-Marie Monkam", role: "Trésorière", profession: "", depuis: "2021", photo: "https://customer-assets-rejwkqb3.emergentagent.net/job_hora-resilience/artifacts/3xjapvh1_Anne%20Marie%20Monkam%2C%20tre%CC%81sorie%CC%80re.webp" },
              { nom: "Armelle Kuemo Mapokam", role: "Administratrice", profession: "Avocate fiscaliste — Kalyx Avocats", depuis: "2021", photo: "https://customer-assets-rejwkqb3.emergentagent.net/job_hora-resilience/artifacts/8ux2kbi4_Armelle%20Kuemo%2C%20administratrice.webp" },
              { nom: "Solédad Victor", role: "Administratrice", profession: "Directrice associée — Pratt & Whitney Canada", depuis: "2025", photo: "https://customer-assets-rejwkqb3.emergentagent.net/job_hora-resilience/artifacts/4w4g0xjg_Victor%20Soledad.jpeg" },
            ].map((m, i) => (
              <Reveal key={m.nom} delay={i * 0.08} className="bg-background">
                <div className="flex h-full items-center gap-5 p-8 transition-colors duration-300 hover:bg-card" data-testid={`administrateur-${i}`}>
                  {m.photo ? (
                    <img
                      src={m.photo}
                      alt={`Portrait de ${m.nom}`}
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-16 shrink-0 rounded-full border-2 border-gold/40 object-cover object-top"
                      data-testid={`administrateur-photo-${i}`}
                    />
                  ) : (
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary/10 font-serif text-lg font-semibold text-primary">
                      {m.nom.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </span>
                  )}
                  <span>
                    <span className="block font-serif text-xl font-semibold leading-tight">{m.nom}</span>
                    <span className="mt-1 block text-sm font-semibold text-primary">{m.role} · depuis {m.depuis}</span>
                    {m.profession && <span className="mt-0.5 block text-sm text-foreground/60">{m.profession}</span>}
                  </span>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4} className="bg-espresso">
              <div className="flex h-full flex-col justify-center p-8 text-ivory" data-testid="gouvernance-transparence">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Transparence</p>
                <p className="mt-4 text-sm leading-relaxed text-ivory/70">
                  Nos rapports annuels sont publics et téléchargeables depuis la page Notre impact.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card py-28" data-testid="about-activites">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Nos activités</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Concrètement, voici ce que nous faisons
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-2">
            {activites.map((a, i) => (
              <Reveal key={a.titre} delay={i * 0.1} className="bg-card">
                <div className="h-full bg-background p-10 transition-colors duration-300 hover:bg-card" data-testid={`activite-${i}`}>
                  <a.icone size={28} strokeWidth={1.5} className="text-primary" />
                  <h3 className="mt-6 font-serif text-2xl font-semibold">{a.titre}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/60">{a.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-espresso py-28 text-ivory" data-testid="about-territoire">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="relative lg:col-span-5">
            <div className="absolute -left-5 -bottom-5 h-full w-full border border-gold/40" aria-hidden="true" />
            <img
              src={MTL_IMG}
              alt="Rue et architecture de Montréal"
              loading="lazy"
              decoding="async"
              className="relative aspect-[4/3] w-full object-cover"
              data-testid="about-montreal-image"
            />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Au cœur du centre-ville</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight">
              Au service des communautés d'ici
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ivory/70">
              Située au centre-ville de Montréal, au 666, rue Sherbrooke Ouest, la Fondation
              travaille main dans la main avec les organismes communautaires, les centres de
              formation et les milieux d'affaires locaux. Nos activités sont pensées pour les
              réalités québécoises : nos exemples, nos références légales et nos fiscalistes
              parlent le langage d'ici.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
